"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Edit, Trash2, FileText, Upload, ChevronDown, Check, X } from "lucide-react"
import type { DocumentClassifier, DocumentEvent, Integration } from "@/types"
import { DataStructuresTab } from "./data-structures-tab"
import { PDFViewer } from "@/components/PdfViewer"
import { EventTriggersSection } from "./event-triggers-section"
import { getCSRFToken } from "@/lib/utils"
import { Link } from "@inertiajs/react"
import { router } from "@inertiajs/core";
import { useStickyState } from "@/lib/use-sticky"

interface ClassificationCardProps {
  classification: DocumentClassifier
  documentEvents: DocumentEvent[]
  integrations: Integration[]
  onEdit: () => void
  onDelete: () => void
  onUpdateDocumentEvents: (events: DocumentEvent[]) => void
  onUpdateClassification?: (classification: DocumentClassifier) => void
}

export function ClassificationCard({
  classification,
  integrations,
  //  onEdit,
  onDelete,
  onUpdateDocumentEvents,
  onUpdateClassification,
}: ClassificationCardProps) {
  console.log("classification prop", classification)
  const [classificationPrompt, setClassificationPrompt] = useState<string>(classification.classification_prompt || "")
  const [showPDF, setShowPDF] = useStickyState(false, 'pdf-viewer')
  console.log("classification", classification)

  const [extractionPrompt, setExtractionPrompt] = useState<string>(classification.extraction_prompt || "")

  const [isOpen, setIsOpen] = useStickyState(false, `classification-card-open-${classification.id}`)
  const [isEditingInfo, setIsEditingInfo] = useState(false)
  const [editName, setEditName] = useState(classification.name)
  const [editDescription, setEditDescription] = useState(classification.description)

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<boolean>(false)

  //const classificationEvents = documentEvents.filter((e) => e.document_classifier_id === classification.id)
  //const automaticEvents = classificationEvents.filter((e) => e.trigger === "automatic")
  //const manualEvents = classificationEvents.filter((e) => e.trigger === "manual")



  const documentEvents = classification.integration_classes
  const automaticEvents = classification.integration_classes.filter((e => e.auto === true))
  const manualEvents = classification.integration_classes.filter((e => e.auto === false))

  const handleClassificationPromptChange = (value: string) => {
    setClassificationPrompt(value)
    if (onUpdateClassification) {
      onUpdateClassification({ ...classification, classification_prompt: value })
    }
  }
  const onSaveClassificationPrompt = () => {
    const csrfToken = getCSRFToken()
    fetch(
      "/document_classifier/" + classification.id, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken || "",

      },
      body: JSON.stringify(classification),
    })
  }
  useEffect(() => {

    if (classification.fax_id !== null) {
      setUploadedFile(true)
    }
  }, [])

  const handleExtractionPromptChange = (value: string) => {
    setExtractionPrompt(value)
    if (onUpdateClassification) {
      onUpdateClassification({ ...classification, extraction_prompt: value })
    }
  }

  const handleSaveInfo = () => {
    if (onUpdateClassification) {
      onUpdateClassification({
        ...classification,
        name: editName,
        description: editDescription,
      })
    }
    setIsEditingInfo(false)
  }

  const handleCancelEdit = () => {
    setEditName(classification.name)
    setEditDescription(classification.description)
    setIsEditingInfo(false)
  }



  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("document", file);

    fetch(`/document_classifier/${classification.id}/upload_example`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": getCSRFToken() || "",
      },
      body: formData,
    }).then((response) => {
      if (response.ok) {
        setUploadedFile(true);
        setIsUploadDialogOpen(false);
      }
    });
  };


  const handleRemoveFile = () => {
    setUploadedFile(null)
  }
  console.log("rendering ClassificationCard with classification:", classification)

  return (
    <>
      <Collapsible className="" open={isOpen} onOpenChange={setIsOpen}>
        <Card className="p-2 py-6 md:p-6  max-w-screen overflow-none rounded-none border-none md:border md:rounded-lg md:border-white w-full">

          <div className="flex items-start justify-between mb-4">
            <CollapsibleTrigger className="flex items-center gap-3 flex-1 text-left">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                {isEditingInfo ? (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="font-semibold"
                      placeholder="Classification name"
                    />
                    <Input
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="text-sm"
                      placeholder="Description"
                    />
                  </div>
                ) : (
                  <>
                    <h3 className="font-semibold">{classification.name}</h3>
                    <p className="text-sm text-muted-foreground">{classification.description}</p>
                  </>
                )}
              </div>
              <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </CollapsibleTrigger>
            <div className="flex gap-2 ml-2">
              {isEditingInfo ? (
                <>
                  <Button variant="ghost" size="icon" onClick={handleSaveInfo}>
                    <Check className="h-4 w-4 text-green-600" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                </>
              ) : (
                <>

                  <Button variant="ghost" size="icon" onClick={() => setIsEditingInfo(true)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={onDelete}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </>
              )}
            </div>
          </div>

          <CollapsibleContent>
            <Accordion type="multiple" className="w-full">
              <AccordionItem value="classification-criteria">
                <AccordionTrigger>Classification Criteria</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 flex flex-col">
                    <p className="text-sm text-muted-foreground">
                      Describe what characteristics or content should be used to identify this document type
                    </p>

                    <Textarea
                      placeholder="e.g., Documents containing referral requests, consultation requests, or patient transfers from other healthcare providers"
                      value={classificationPrompt}
                      onChange={(e) => handleClassificationPromptChange(e.target.value)}
                      rows={14}
                    />
                    <Button onClick={onSaveClassificationPrompt} className="ml-auto">Save</Button>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="extracted-fields">
                <AccordionTrigger>Extracted Fields</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 mb-6">
                    <div className="space-y-3 flex flex-col">
                      <p className="text-sm text-muted-foreground">
                        Describe what data fields you want to extract from this document type
                      </p>

                      <Textarea
                        placeholder="e.g., Extract patient name, date of birth, referring physician, reason for referral, urgency level, and contact information"
                        value={extractionPrompt}
                        onChange={(e) => handleExtractionPromptChange(e.target.value)}
                        rows={12}
                        className="max-h-[600px]"
                      />

                      <Button onClick={onSaveClassificationPrompt} className="ml-auto">Save</Button>
                    </div>
                  </div>

                  <DataStructuresTab classification={classification} onUpdate={onUpdateClassification} />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="event-triggers">
                <AccordionTrigger>Event Triggers</AccordionTrigger>
                <AccordionContent>
                  <EventTriggersSection
                    classification={classification}
                    automaticEvents={automaticEvents}
                    manualEvents={manualEvents}
                    allDocumentEvents={documentEvents}
                    integrations={integrations}
                    onUpdateDocumentEvents={onUpdateDocumentEvents}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="mt-4 pt-4 border-t">
              {!uploadedFile ? (
                <Button variant="outline" className="w-full bg-transparent" onClick={() => setIsUploadDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Example
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800" onClick={() => setShowPDF(true)} >
                    <Check className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-900 dark:text-green-100">File uploaded (click to view)</p>
                      <p className="text-xs text-green-700 dark:text-green-300 truncate">{uploadedFile.name}</p>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleRemoveFile}
                      className="h-8 w-8 flex-shrink-0 hover:bg-green-100 dark:hover:bg-green-900"
                    >
                      <X className="h-4 w-4 text-green-700 dark:text-green-300" />
                    </Button>
                  </div>
                  {
                    classification?.status == "done" ? (
                      <Link href={"/document_classifier/" + classification.id + "/preview"} target="_blank">
                        <Button variant="outline" className="w-full bg-transparent" >
                          Preview UI
                        </Button>
                      </Link>
                    ) : (
                      <Button variant="outline" className="w-full bg-transparent" disabled>
                        Preview UI (available after upload and processing)
                      </Button>

                    )
                  }

                </div>
              )}
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Example Document</DialogTitle>
            <DialogDescription>Upload a sample PDF document to test the extraction process</DialogDescription>
          </DialogHeader>

          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-4">Click to upload or drag and drop a PDF file</p>
            <Input type="file" accept="application/pdf" onChange={handleFileUpload} className="cursor-pointer" />
          </div>
        </DialogContent>
      </Dialog>
      <Sheet open={showPDF} onOpenChange={setShowPDF}>
        <SheetContent side="right" className="lg:w-[60vw] h-screen sm:w-screen sm:max-w-none p-0 flex flex-col">
          <SheetHeader className="border-b px-4 py-3">
            <SheetTitle>PDF Viewer</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-hidden">
            <PDFViewer pdfUrl={`${classification.fax_id}`} fileName={`record.pdf`} />
          </div>
        </SheetContent>
      </Sheet>

    </>
  )
}

